import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateProject,
  ProjectEvent,
  ProjectEventList,
  ProjectListItem,
  ProjectStatus,
  UpdateProject,
} from '@mys/contracts';
import { api } from './api.js';
import { formatDate } from './format.js';

/* ============================================================================
   Project queries.
   ========================================================================== */

export const PROJECT_STATUSES: ProjectStatus[] = ['brief', 'preview', 'build', 'live'];

/** What each stage is called on screen, in the owner's words. */
export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  brief: 'Brief',
  preview: 'Preview',
  build: 'Build',
  live: 'Live',
};

export interface ProjectListResponse {
  ok: true;
  items: ProjectListItem[];
  total: number;
  page: number;
  limit: number;
  counts: Partial<Record<ProjectStatus, number>>;
  overdue: number;
}

export interface ProjectFilters {
  status?: ProjectStatus;
  clientId?: string;
  overdue?: boolean;
  q?: string;
}

export function useProjects(filters: ProjectFilters, limit = 100) {
  return useQuery({
    queryKey: ['projects', filters, limit],
    queryFn: ({ signal }) => {
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.clientId) params.set('clientId', filters.clientId);
      if (filters.overdue) params.set('overdue', 'true');
      if (filters.q) params.set('q', filters.q);
      params.set('limit', String(limit));
      return api.get<ProjectListResponse>(`/api/projects?${params.toString()}`, signal);
    },
  });
}

export function useProject(id: string) {
  return useQuery({
    queryKey: ['project', id],
    queryFn: ({ signal }) =>
      api.get<{ ok: true; project: ProjectListItem }>(`/api/projects/${id}`, signal),
  });
}

/**
 * What has happened to this project, oldest first.
 *
 * Its own query rather than part of the project payload: the board and the
 * client record both read a project, and neither wants a history attached to
 * every row.
 */
export function useProjectEvents(id: string) {
  return useQuery({
    queryKey: ['project-events', id],
    queryFn: ({ signal }) => api.get<ProjectEventList>(`/api/projects/${id}/events`, signal),
  });
}

/** Anything that changes a project also changes a client's record and the
    board's counts, so both are invalidated rather than patched by hand. */
function invalidate(client: ReturnType<typeof useQueryClient>, clientId?: string) {
  void client.invalidateQueries({ queryKey: ['projects'] });
  void client.invalidateQueries({ queryKey: ['clients'] });
  if (clientId) void client.invalidateQueries({ queryKey: ['client', clientId] });
}

export function useCreateProject() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateProject) =>
      api.post<{ ok: true; project: ProjectListItem }>('/api/projects', input),
    onSuccess: (_data, input) => invalidate(client, input.clientId),
  });
}

export function useUpdateProject(id: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (changes: UpdateProject) =>
      api.patch<{ ok: true; project: ProjectListItem }>(`/api/projects/${id}`, changes),
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: ['project', id] });
      invalidate(client, data.project.clientId);
    },
  });
}

/**
 * One line of history, in the owner's words.
 *
 * Pure, so it is testable without a component. Setting a date and clearing one
 * read differently on purpose: "removed" is the line someone scanning for a
 * vanished promise is actually looking for, and it would be invisible if both
 * collapsed into "changed".
 */
export function describeEvent(event: ProjectEvent): string {
  if (event.kind === 'status_changed') {
    const from = event.fromValue ? PROJECT_STATUS_LABELS[event.fromValue as ProjectStatus] : null;
    const to = event.toValue ? PROJECT_STATUS_LABELS[event.toValue as ProjectStatus] : null;
    return from ? `${from} → ${to}` : `${to}`;
  }

  const from = event.fromValue ? formatDate(event.fromValue) : null;
  const to = event.toValue ? formatDate(event.toValue) : null;
  if (!from) return `Promised date set: ${to}`;
  if (!to) return `Promised date removed (was ${from})`;
  return `Promised date ${from} → ${to}`;
}

/** The next stage, or null once live. Brief → preview → build → live. */
export function nextProjectStatus(status: ProjectStatus): ProjectStatus | null {
  const index = PROJECT_STATUSES.indexOf(status);
  if (index < 0 || index >= PROJECT_STATUSES.length - 1) return null;
  return PROJECT_STATUSES[index + 1] ?? null;
}

/**
 * Days late, as a positive number. Null when nothing is owed.
 *
 * Only meaningful for a project the API has already flagged `overdue`; the
 * flag is the judgement and this is just how it reads on screen.
 */
export function daysLate(project: ProjectListItem, now = new Date()): number | null {
  if (!project.overdue || !project.promisedDate) return null;
  const diff = now.getTime() - new Date(project.promisedDate).getTime();
  return Math.max(1, Math.floor(diff / 86_400_000));
}
